/**
 * MARKING PART OF A REACTION.
 *
 * ── WHAT A MARK IS, AND WHAT IT IS NOT ────────────────────────────────────────────────────────
 * Colour and strikethrough MARK text that is already there. An oxidation number ANNOTATES it —
 * it puts something above an atom that was not written before. They share this machinery because
 * they share a shape, a range plus a value, not because they are the same act.
 *
 * Underline is deliberately absent. It would be redundant with colour: of the thirty swatches,
 * the ten the formula editors now offer clear 3:1 against the body text beside them, so they
 * separate by LIGHTNESS and a colour-blind teacher sees the mark without needing the hue. Crossing
 * out is a different act from highlighting — it says removed, not look here — so it stays.
 *
 * ── WHY A MARK IS A RANGE AND NOT A PROPERTY OF THE TOKEN ─────────────────────────────────────
 * Colour used to belong to a whole token, which was broader than the notation requires. Measured:
 * a boundary falling anywhere EXCEPT between an atom and the digits that subscript it renders
 * byte-identically to plain. `CH3-[c:COOH]` is fine; two groups in one species each take their
 * own colour; only `[c:CO]2` loses the subscript, because mhchem makes a digit a subscript by
 * meeting it bare and a wrap stops it being bare.
 *
 * So the editor marks ranges and SNAPS the ones that would break — see `snapRange`.
 *
 * ── A SPAN ACROSS TOKENS IS A UI IDEA, NOT A STORED ONE ───────────────────────────────────────
 * Marking `2H2O + O2` stores a mark on each of the three tokens, never one wrap around the lot.
 * A wrap containing spaces comes back through `splitAtDepthZero` as ONE token, which would fuse
 * three species into a single editable unit. Adjacent tokens sharing a colour already look like
 * one span, which is all a teacher needs.
 */

import { lewisValue, parseLewisValue, readLewis, writeLewis } from './reaction-lewis';

export type MarkKind =
  'color' | 'strike' | 'italic' | 'lewis' | 'oxidation' | 'bar' | 'annotation';

/** A range over a token's PLAIN text, with the value the kind needs. */
export type Mark = {
  start: number;
  end: number;
  kind: MarkKind;
  /** A hex for `color`, the number for `oxidation`, four digits for `lewis` (see
   *  `reaction-lewis`), the words themselves for `annotation`. `bar` and the rest carry none. */
  value?: string;
};

/** The order marks nest in when written out — later kinds wrap earlier ones. */
const NESTING: MarkKind[] =
  ['bar', 'italic', 'lewis', 'oxidation', 'annotation', 'strike', 'color'];

const HEX = /^#[0-9A-Fa-f]{3,6}$/;

// ── Writing marks out ─────────────────────────────────────────────────────────

function wrap(kind: MarkKind, inner: string, value?: string): string {
  switch (kind) {
    // Agavi's own syntax, which `renderChemFormula` turns into `\textcolor` AROUND a `\ce{}`.
    case 'color': return `[${value}:${inner}]`;
    // `\cancel` needs maths, and the inner `\ce{}` is what keeps the subscripts: measured, a
    // struck `NO3-` still draws its 3 low and its charge high.
    case 'strike': return `$\\cancel{\\ce{${inner}}}$`;
    case 'oxidation': return `$\\overset{${value}}{\\ce{${inner}}}$`;
    /* The ONLY wrap with no inner `\ce{}`, and it has no choice: `\ce` sets upright, so
       `$\mathit{\ce{cis}}$` renders upright and the mark does nothing at all. Measured. */
    case 'italic': return `$\\mathit{${inner}}$`;
    /* A group of one or two dots is the same `\overset` an oxidation number uses. It is
       unambiguous because the oxidation popup offers a fixed list of states and dots are not
       one of them. Written by the module that reads it — see `reaction-lewis`. */
    case 'lewis': return writeLewis(inner, parseLewisValue(value ?? ''));
    /* The antiparticle bar. Innermost of all the wraps, because it belongs to the SPECIES —
       a struck antiproton is a bar with a line through it, never a bar over a struck p. */
    case 'bar': return `$\\bar{\\ce{${inner}}}$`;
    /* Words under a species, which is the one mark here whose content is PROSE.
       `\text{}` and not `\ce{}`: `\underset{calcium carbonate}{…}` inside a reaction reads
       Ca, C and O as elements and sets the digits low, so the gloss comes out as chemistry. */
    case 'annotation': return `$\\underset{\\text{${value}}}{\\ce{${inner}}}$`;
  }
}

/**
 * Write a token's text back out with its marks wrapped around the right characters.
 *
 * Built in ONE recursive pass rather than a pass per kind. A pass per kind looked simpler and was
 * wrong: the first pass rewrites the string, so the second pass's offsets point into text that no
 * longer exists and a fragment that was both struck and coloured came out as
 * `[#A324BA:$\can]cel{...}`. Here a range is emitted once, with everything inside it built first,
 * and two marks sharing a range nest in the order the renderer accepts — colour outermost,
 * measured in both directions.
 */
function build(text: string, marks: Mark[], from: number, to: number): string {
  const inRange = marks.filter(m => m.start >= from && m.end <= to);
  const whole = inRange.filter(m => m.start === from && m.end === to);
  const inner = inRange.filter(m => !(m.start === from && m.end === to));

  if (whole.length) {
    let out = build(text, inner, from, to);
    for (const kind of NESTING) {
      const m = whole.find(x => x.kind === kind);
      if (m) out = wrap(kind, out, m.value);
    }
    return out;
  }

  /* The ranges directly inside this one: those no other inner range strictly contains. */
  const tops: Mark[] = [];
  for (const m of inner) {
    const contained = inner.some(o =>
      (o.start < m.start && o.end >= m.end) || (o.start <= m.start && o.end > m.end));
    if (contained) continue;
    if (!tops.some(t => t.start === m.start && t.end === m.end)) tops.push(m);
  }
  tops.sort((a, b) => a.start - b.start);

  let out = '';
  let i = from;
  for (const t of tops) {
    if (t.start < i) continue;   // an overlap no nesting can express; the first one wins
    out += text.slice(i, t.start) + build(text, inner, t.start, t.end);
    i = t.end;
  }
  return out + text.slice(i, to);
}

export function serializeMarks(text: string, marks: Mark[] = []): string {
  const valid = marks.filter(m => m.end > m.start && m.start >= 0 && m.end <= text.length);
  return valid.length ? build(text, valid, 0, text.length) : text;
}

// ── Reading marks back ────────────────────────────────────────────────────────

/** The index just past the `{…}` group that starts at `open`, or -1 if it never closes. */
function matchBrace(src: string, open: number): number {
  let depth = 0;
  for (let i = open; i < src.length; i++) {
    if (src[i] === '{') depth++;
    else if (src[i] === '}' && --depth === 0) return i + 1;
  }
  return -1;
}

/** The index just past the `[…]` that starts at `open`, counting nested brackets. */
function matchBracket(src: string, open: number): number {
  let depth = 0;
  for (let i = open; i < src.length; i++) {
    if (src[i] === '[') depth++;
    else if (src[i] === ']' && --depth === 0) return i + 1;
  }
  return -1;
}

/**
 * Pull the wrappers off a stored token, recording where each one sat in the plain text.
 *
 * Anything that is not one of the three recognised forms is left alone as literal text. That is
 * deliberate and not laziness: a teacher may have typed `[red:…]` or any other bracket by hand,
 * and mhchem's annotated arrow `->[Fe:cat]` is bracket-word-colon too. Reading a catalyst label
 * as a colour would be worse than leaving an unrecognised wrapper as what it says.
 */
export function parseMarks(src: string): { text: string; marks: Mark[] } {
  let text = '';
  const marks: Mark[] = [];

  const eat = (s: string): void => {
    let i = 0;
    while (i < s.length) {
      const rest = s.slice(i);

      // [#rrggbb:…]
      const colour = /^\[(#[0-9A-Fa-f]{3,6}):/.exec(rest);
      if (colour) {
        const close = matchBracket(s, i);
        const inner = close > 0 ? s.slice(i + colour[0].length, close - 1) : '';
        /* An EMPTY wrap is left as the characters it is. Consuming it would delete what the
           teacher typed and leave nothing to show for it. */
        if (close > 0 && inner) {
          const start = text.length;
          eat(inner);
          marks.push({ start, end: text.length, kind: 'color', value: colour[1] });
          i = close;
          continue;
        }
      }

      /* _\textcolor{#rrggbb}{3} — a COLOURED SUBSCRIPT.
         The only partial-token colour the notation allows: mhchem keeps the subscript when the
         digit is coloured this way and loses it when the species is split into two wraps. Read
         here so it is one mark like any other, which is what lets the speech module say
         "highlighted 3" instead of spelling out backslash-textcolor. */
      const subColour = /^_\\textcolor\{(#[0-9A-Fa-f]{3,6})\}\{(\d+)\}/.exec(rest);
      if (subColour) {
        const start = text.length;
        text += subColour[2];
        marks.push({ start, end: text.length, kind: 'color', value: subColour[1] });
        i += subColour[0].length;
        continue;
      }

      // $\cancel{\ce{…}}$
      if (rest.startsWith('$\\cancel{\\ce{')) {
        const inner = matchBrace(s, i + '$\\cancel{'.length + '\\ce'.length);
        const outer = matchBrace(s, i + '$\\cancel'.length);
        if (inner > 0 && outer > 0 && s.slice(outer, outer + 1) === '$') {
          const start = text.length;
          eat(s.slice(i + '$\\cancel{\\ce{'.length, inner - 1));
          if (text.length > start) marks.push({ start, end: text.length, kind: 'strike' });
          i = outer + 1;
          continue;
        }
      }

      // $\mathit{…}$
      if (rest.startsWith('$\\mathit{')) {
        const close = matchBrace(s, i + '$\\mathit'.length);
        if (close > 0 && s.slice(close, close + 1) === '$') {
          const start = text.length;
          eat(s.slice(i + '$\\mathit{'.length, close - 1));
          if (text.length > start) marks.push({ start, end: text.length, kind: 'italic' });
          i = close + 1;
          continue;
        }
      }

      /* $\bar{\ce{…}}$ — the antiparticle bar. Not `\bar{p}{}`, which is what the antineutrino
         KEY inserts: that one is an atom with its own glyph, and a mark that claimed the same
         spelling would swallow it on the way back in. */
      if (rest.startsWith('$\\bar{\\ce{')) {
        const ceEnd = matchBrace(s, i + '$\\bar'.length + '{\\ce'.length);
        const outer = matchBrace(s, i + '$\\bar'.length);
        if (ceEnd > 0 && outer > 0 && s.slice(outer, outer + 1) === '$') {
          const start = text.length;
          eat(s.slice(i + '$\\bar{\\ce{'.length, ceEnd - 1));
          if (text.length > start) marks.push({ start, end: text.length, kind: 'bar' });
          i = outer + 1;
          continue;
        }
      }

      /* $\underset{\text{…}}{\ce{…}} — words under a species. BEFORE the lone pair, whose own
         underset carries `..` and would otherwise claim this one's opening. */
      if (rest.startsWith('$\\underset{\\text{')) {
        /* `{\text{…}}{\ce{…}}`: the species group starts where the WORDS group ends, not
           where the words themselves do. See the same read in `reaction-doc`. */
        const wordsAt = i + '$\\underset'.length;
        const words = matchBrace(s, wordsAt + '{\\text'.length);
        const speciesAt = matchBrace(s, wordsAt);
        if (words > 0 && speciesAt > 0 && s.slice(speciesAt).startsWith('{\\ce{')) {
          const ceEnd = matchBrace(s, speciesAt + '{\\ce'.length);
          const outer = matchBrace(s, speciesAt);
          if (ceEnd > 0 && outer > 0 && s.slice(outer, outer + 1) === '$') {
            const value = s.slice(i + '$\\underset{\\text{'.length, words - 1);
            const start = text.length;
            eat(s.slice(speciesAt + '{\\ce{'.length, ceEnd - 1));
            if (text.length > start) marks.push({ start, end: text.length, kind: 'annotation', value });
            i = outer + 1;
            continue;
          }
        }
      }

      /* Lewis dots — BEFORE the oxidation rule below, which would otherwise read a dot group
         as a state and have the speech module say so. Read by the module that writes them. */
      const lewis = readLewis(s, i);
      if (lewis) {
        const start = text.length;
        eat(lewis.inner);
        if (text.length > start) {
          marks.push({ start, end: text.length, kind: 'lewis', value: lewisValue(lewis.dots) });
        }
        i = lewis.end;
        continue;
      }

      // $\overset{n}{\ce{…}}$
      if (rest.startsWith('$\\overset{')) {
        const numEnd = matchBrace(s, i + '$\\overset'.length);
        if (numEnd > 0 && s.slice(numEnd).startsWith('{\\ce{')) {
          const ceEnd = matchBrace(s, numEnd + '{\\ce'.length);
          const outer = matchBrace(s, numEnd);
          if (ceEnd > 0 && outer > 0 && s.slice(outer, outer + 1) === '$') {
            const value = s.slice(i + '$\\overset{'.length, numEnd - 1);
            const start = text.length;
            eat(s.slice(numEnd + '{\\ce{'.length, ceEnd - 1));
            if (text.length > start) marks.push({ start, end: text.length, kind: 'oxidation', value });
            i = outer + 1;
            continue;
          }
        }
      }

      text += s[i];
      i++;
    }
  };

  eat(src);
  return { text, marks: marks.sort((a, b) => a.start - b.start || a.end - b.end) };
}

// ── Snapping a selection to somewhere the notation survives ───────────────────

const isDigit = (c: string | undefined) => !!c && c >= '0' && c <= '9';
const isLower = (c: string | undefined) => !!c && c >= 'a' && c <= 'z';
const isUpper = (c: string | undefined) => !!c && c >= 'A' && c <= 'Z';

/** Is `i` sitting inside a run of digits that is subscripting whatever precedes it? */
function insideSubscript(text: string, i: number): boolean {
  if (!isDigit(text[i])) return false;
  let j = i;
  while (j > 0 && isDigit(text[j - 1])) j--;
  const before = text[j - 1];
  return isUpper(before) || isLower(before) || before === ')' || before === ']';
}

/** Walk back from a digit run over the thing it subscripts: an element symbol or a bracketed group. */
function startOfSubscripted(text: string, i: number): number {
  let j = i;
  while (j > 0 && isDigit(text[j - 1])) j--;
  const before = text[j - 1];
  if (before === ')' || before === ']') {
    const open = before === ')' ? '(' : '[';
    let depth = 0;
    for (let k = j - 1; k >= 0; k--) {
      if (text[k] === before) depth++;
      else if (text[k] === open && --depth === 0) return k;
    }
    return 0;
  }
  // An element symbol is an uppercase letter and at most one lowercase after it.
  if (isLower(before)) return isUpper(text[j - 2]) ? j - 2 : j - 1;
  if (isUpper(before)) return j - 1;
  return j;
}

/**
 * Widen a selection to the nearest place a mark survives.
 *
 * The one thing that cannot be marked is a subscript with no atom attached, so a boundary landing
 * between an atom and its digits moves: the START back over the atom, the END forward past the
 * digits. Selecting the `4` of `Si4` marks `Si4`, which is almost certainly what was meant —
 * "look at this atom and its count" rather than "look at this number".
 *
 * The widening is SHOWN before the mark applies, so a teacher sees what they are about to mark
 * rather than discovering it afterwards.
 */
export function snapRange(text: string, start: number, end: number): [number, number] {
  let a = Math.max(0, Math.min(start, end));
  let b = Math.min(text.length, Math.max(start, end));
  if (a === b) return [a, b];
  if (insideSubscript(text, a)) a = startOfSubscripted(text, a);
  while (b < text.length && insideSubscript(text, b)) b++;
  return [a, b];
}

// ── Keeping marks true as the text changes ────────────────────────────────────

/** Drop empty marks and merge neighbours that say the same thing. */
export function tidyMarks(marks: Mark[]): Mark[] {
  const kept = marks.filter(m => m.end > m.start).sort((a, b) => a.start - b.start || a.end - b.end);
  const out: Mark[] = [];
  for (const m of kept) {
    const prev = out[out.length - 1];
    if (prev && prev.kind === m.kind && prev.value === m.value && prev.end >= m.start) {
      prev.end = Math.max(prev.end, m.end);
    } else out.push({ ...m });
  }
  return out;
}

/**
 * Apply a mark over a range, clearing what the same kind had there.
 *
 * Same kind replaces: two colours over the same characters is not a thing, so the older one is
 * cut back around the new range rather than left to fight it. A DIFFERENT kind survives
 * untouched, which is what lets a fragment be both crossed out and coloured.
 */
export function applyMark(
  marks: Mark[], start: number, end: number, kind: MarkKind, value?: string,
): Mark[] {
  const [a, b] = [Math.min(start, end), Math.max(start, end)];
  if (a === b) return marks;
  const next: Mark[] = [];
  for (const m of marks) {
    if (m.kind !== kind || m.end <= a || m.start >= b) { next.push(m); continue; }
    if (m.start < a) next.push({ ...m, end: a });
    if (m.end > b) next.push({ ...m, start: b });
  }
  next.push({ start: a, end: b, kind, ...(value !== undefined ? { value } : {}) });
  return tidyMarks(next);
}

/** Take a kind of mark off a range, leaving whatever it overlapped on either side. */
export function clearMark(marks: Mark[], start: number, end: number, kind: MarkKind): Mark[] {
  const [a, b] = [Math.min(start, end), Math.max(start, end)];
  const next: Mark[] = [];
  for (const m of marks) {
    if (m.kind !== kind || m.end <= a || m.start >= b) { next.push(m); continue; }
    if (m.start < a) next.push({ ...m, end: a });
    if (m.end > b) next.push({ ...m, start: b });
  }
  return tidyMarks(next);
}

/** What a kind of mark says over a range, when it covers the whole of it. */
export function markAt(marks: Mark[], start: number, end: number, kind: MarkKind): Mark | null {
  const [a, b] = [Math.min(start, end), Math.max(start, end)];
  return marks.find(m => m.kind === kind && m.start <= a && m.end >= b && b > a) ?? null;
}

/**
 * Move marks to follow an edit that replaced `[from, to)` with `length` new characters.
 *
 * A mark shrinks, grows or disappears with the characters it was put on, rather than being
 * recomputed — which is the only way a colour stays on the group a teacher put it on while they
 * fix a typo inside it.
 */
export function shiftMarks(marks: Mark[], from: number, to: number, length: number): Mark[] {
  const delta = length - (to - from);
  const moved = marks.map(m => {
    const start = m.start >= to ? m.start + delta : (m.start > from ? from : m.start);
    const end = m.end >= to ? m.end + delta : (m.end > from ? from + length : m.end);
    return { ...m, start, end };
  });
  return tidyMarks(moved);
}

/** Shift marks into the coordinates of a token that starts at `offset` of the original. */
export function sliceMarks(marks: Mark[], from: number, to: number): Mark[] {
  return tidyMarks(marks.map(m => ({
    ...m,
    start: Math.max(0, Math.min(m.start, to) - from),
    end: Math.max(0, Math.min(m.end, to) - from),
  })));
}

/** True when a value is storable as a colour — the picker only ever writes hex. */
export const isHex = (v: string) => HEX.test(v);
